import axios, { type AxiosProgressEvent } from 'axios';
import { apiClient } from '@/api/client';

/** Presigned S3 PUT descriptor returned by the backend (mirrors PresignedUpload). */
export interface PresignedUpload {
  uploadUrl: string;
  fileKey: string;
  contentType: string;
  maxBytes: number;
  expiresInSeconds: number;
}

export type UploadKind =
  | 'pitchDeck'
  | 'pitchVideo'
  | 'logo'
  | 'investorProfilePhoto'
  | 'eventCoverPhoto'
  | 'partnerLogo'
  | 'partnerCoverPhoto'
  | 'partnerListingCover'
  | 'partnerOfferCoverPhoto'
  | 'resourceFile'
  | 'resourceCoverImage'
  | 'individualAvatar'
  | 'investorLogo'
  | 'contentSectionImage';

/**
 * Upload purposes - the mirror of the backend `UPLOAD_PURPOSES` registry key.
 * A new file type is one entry here plus one backend policy entry; the endpoint
 * and this client are purpose-driven and never grow per type.
 *
 * `eventCoverPhoto` -> `event_cover_photo` (`Backend/src/modules/file-upload/upload-policies.ts`,
 * PNG/JPG, 5 MB) backs the admin event form's cover photo field (MYN-92 Task 2.2).
 */
const UPLOAD_PURPOSE_BY_KIND: Record<UploadKind, string> = {
  pitchDeck: 'startup_pitch_deck',
  pitchVideo: 'startup_pitch_video',
  logo: 'startup_logo',
  investorProfilePhoto: 'investor_profile_photo',
  eventCoverPhoto: 'event_cover_photo',
  partnerLogo: 'trusted_partner_logo',
  partnerCoverPhoto: 'trusted_partner_cover_photo',
  // FR-116: the optional directory-card cover (PNG/JPG, 5 MB, prefix `partners/listing-covers`).
  partnerListingCover: 'trusted_partner_listing_cover',
  partnerOfferCoverPhoto: 'trusted_partner_offer_cover_photo',
  // Resource upload purposes (MYN-100): PDF/DOCX/XLSX/image files and optional
  // cover images for resource items. Purpose keys mirror the backend registry.
  resourceFile: 'resource_file',
  resourceCoverImage: 'resource_cover_image',
  // MYN-175. `individualAvatar` is the person's own picture (prefix
  // `users/avatars`), so it follows them across every entity they belong to.
  // `investorLogo` is the investor FIRM's mark (prefix `investors/logos`) and
  // is deliberately distinct from `investorProfilePhoto`, the contact's
  // headshot - the backend gives them separate policies and separate key
  // prefixes, and a key valid for one is rejected for the other.
  individualAvatar: 'individual_avatar',
  investorLogo: 'investor_logo',
  // Public page content-manager section images (MYN-196, Backend
  // Backend/docs/specs/features/public-pages/02-design.md §18), prefix `pages/sections`.
  contentSectionImage: 'content_section_image',
};

const FALLBACK_CONTENT_TYPE = 'application/octet-stream';

export interface UploadResult {
  fileKey: string;
  /** The browser-reported name of the file the member chose, for display only. */
  fileName: string;
  sizeBytes: number;
}

/** Reports whole-percent upload progress, 0-100. Never called with NaN. */
export type UploadProgressCallback = (percent: number) => void;

export class FileTooLargeError extends Error {
  constructor(readonly maxBytes: number) {
    super('FILE_TOO_LARGE');
    this.name = 'FileTooLargeError';
  }
}

async function requestUploadUrl(
  purpose: string,
  contentType: string,
): Promise<PresignedUpload> {
  const { data } = await apiClient.post<PresignedUpload>('/uploads/presign', {
    purpose,
    contentType,
  });
  return data;
}

/**
 * Upload a file straight to S3 via a backend-issued presigned URL (mnr hard
 * rule #3 - bytes never pass through our API). Uses a bare axios call so the
 * app's bearer-token interceptor does not corrupt the signed S3 request.
 *
 * `onProgress` receives whole percentages as the bytes go out. It is silent
 * when the transfer reports no total - a proxy that strips `content-length`
 * emits progress events without one, and a percentage of an unknown total is
 * either a division by zero or a bar frozen at 0%. Callers fall back to an
 * indeterminate indicator in that case.
 *
 * @throws FileTooLargeError when the file exceeds the server-declared cap.
 */
export async function uploadFile(
  kind: UploadKind,
  file: File,
  onProgress?: UploadProgressCallback,
): Promise<UploadResult> {
  const contentType = file.type || FALLBACK_CONTENT_TYPE;
  const presign = await requestUploadUrl(UPLOAD_PURPOSE_BY_KIND[kind], contentType);

  if (file.size > presign.maxBytes) {
    throw new FileTooLargeError(presign.maxBytes);
  }

  await axios.put(presign.uploadUrl, file, {
    headers: { 'Content-Type': presign.contentType },
    onUploadProgress: (event: AxiosProgressEvent) => {
      if (!onProgress || !event.total) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });

  return { fileKey: presign.fileKey, fileName: file.name, sizeBytes: file.size };
}
